import { CheckSquare, FileText, Loader2, Scale, Search, User, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, type SearchResults } from '@/lib/api-client';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/lib/use-permissions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  canceled: 'Cancelada',
};

function formatDueDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR');
}

// ---------------------------------------------------------------------------
// Tipos de hit unificado (para navegação por teclado)
// ---------------------------------------------------------------------------

type HitKind = 'client' | 'case' | 'document' | 'task';

interface FlatHit {
  kind: HitKind;
  id: string;
}

function buildFlatList(data: SearchResults): FlatHit[] {
  return [
    ...data.clients.map((c) => ({ kind: 'client' as const, id: c.id })),
    ...data.cases.map((c) => ({ kind: 'case' as const, id: c.id })),
    ...data.documents.map((d) => ({ kind: 'document' as const, id: d.id })),
    ...data.tasks.map((t) => ({ kind: 'task' as const, id: t.id })),
  ];
}

// ---------------------------------------------------------------------------
// Sub-componentes de seção
// ---------------------------------------------------------------------------

interface SectionProps {
  activeId: string | null;
  onHover: (id: string) => void;
  onSelect: (kind: HitKind, id: string) => void;
}

function ClientSection({
  clients,
  activeId,
  onHover,
  onSelect,
}: SectionProps & { clients: SearchResults['clients'] }) {
  if (clients.length === 0) return null;
  return (
    <div>
      <SectionHeader icon={<User className="h-3.5 w-3.5" />} label={`Clientes (${clients.length})`} />
      {clients.map((c) => (
        <HitRow
          key={c.id}
          active={activeId === c.id}
          onMouseEnter={() => onHover(c.id)}
          onClick={() => onSelect('client', c.id)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--accent-fg)] text-sm font-semibold">
              {c.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--fg)]">{c.name}</p>
              <p className="truncate text-xs text-[var(--fg-muted)]">
                {[c.document, c.email ?? c.phone].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        </HitRow>
      ))}
    </div>
  );
}

function CaseSection({
  cases,
  activeId,
  onHover,
  onSelect,
}: SectionProps & { cases: SearchResults['cases'] }) {
  if (cases.length === 0) return null;
  return (
    <div>
      <SectionHeader icon={<Scale className="h-3.5 w-3.5" />} label={`Processos (${cases.length})`} />
      {cases.map((c) => (
        <HitRow
          key={c.id}
          active={activeId === c.id}
          onMouseEnter={() => onHover(c.id)}
          onClick={() => onSelect('case', c.id)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg-muted)]">
              <Scale className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--fg)]">
                {c.cnjNumber ?? 'Sem número'}
              </p>
              <p className="truncate text-xs text-[var(--fg-muted)]">
                {[c.actionType, c.clientName].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        </HitRow>
      ))}
    </div>
  );
}

function DocumentSection({
  documents,
  activeId,
  onHover,
  onSelect,
}: SectionProps & { documents: SearchResults['documents'] }) {
  if (documents.length === 0) return null;
  return (
    <div>
      <SectionHeader icon={<FileText className="h-3.5 w-3.5" />} label={`Documentos (${documents.length})`} />
      {documents.map((d) => (
        <HitRow
          key={d.id}
          active={activeId === d.id}
          onMouseEnter={() => onHover(d.id)}
          onClick={() => onSelect('document', d.id)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg-muted)]">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--fg)]">{d.title}</p>
              <p className="truncate text-xs text-[var(--fg-muted)]">
                {[d.clientName, formatRelativeTime(d.createdAt)].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        </HitRow>
      ))}
    </div>
  );
}

function TaskSection({
  tasks,
  activeId,
  onHover,
  onSelect,
}: SectionProps & { tasks: SearchResults['tasks'] }) {
  if (tasks.length === 0) return null;
  return (
    <div>
      <SectionHeader icon={<CheckSquare className="h-3.5 w-3.5" />} label={`Tarefas (${tasks.length})`} />
      {tasks.map((t) => (
        <HitRow
          key={t.id}
          active={activeId === t.id}
          onMouseEnter={() => onHover(t.id)}
          onClick={() => onSelect('task', t.id)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg-muted)]">
              <CheckSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-[var(--fg)]">{t.title}</p>
                <span className="shrink-0 rounded-full border border-[var(--border)] px-2 py-0.5 text-[10px] text-[var(--fg-muted)]">
                  {STATUS_LABEL[t.status] ?? t.status}
                </span>
              </div>
              {t.dueDate && (
                <p className="text-xs text-[var(--fg-muted)]">
                  Prazo: {formatDueDate(t.dueDate)}
                </p>
              )}
            </div>
          </div>
        </HitRow>
      ))}
    </div>
  );
}

function SectionHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
      {icon}
      {label}
    </div>
  );
}

function HitRow({
  active,
  onMouseEnter,
  onClick,
  children,
}: {
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn(
        'w-full cursor-pointer px-3 py-2 text-left transition-colors',
        active
          ? 'border-l-2 border-[var(--accent)] bg-[var(--surface-muted)]'
          : 'border-l-2 border-transparent hover:bg-[var(--surface-muted)]',
      )}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal principal
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SearchModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const [activeId, setActiveId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasModule } = usePermissions();

  // Resetar estado ao fechar/abrir
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveId(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => apiClient.searchGlobal(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const flatList: FlatHit[] = data ? buildFlatList(data) : [];

  const handleSelect = useCallback(
    (kind: HitKind, id: string) => {
      onClose();
      switch (kind) {
        case 'client':
          navigate('/escritorio/clientes/' + id);
          break;
        case 'case':
          navigate('/escritorio/processos/' + id);
          break;
        case 'document':
          navigate('/escritorio/documentos/instancias/' + id);
          break;
        case 'task':
          navigate('/escritorio/atividades?taskId=' + id);
          break;
      }
    },
    [navigate, onClose],
  );

  // Navegação por teclado
  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (flatList.length === 0) return;

        const currentIdx = activeId ? flatList.findIndex((h) => h.id === activeId) : -1;
        let nextIdx: number;
        if (e.key === 'ArrowDown') {
          nextIdx = currentIdx < flatList.length - 1 ? currentIdx + 1 : 0;
        } else {
          nextIdx = currentIdx > 0 ? currentIdx - 1 : flatList.length - 1;
        }
        setActiveId(flatList[nextIdx].id);
        return;
      }

      if (e.key === 'Enter' && activeId) {
        e.preventDefault();
        const hit = flatList.find((h) => h.id === activeId);
        if (hit) handleSelect(hit.kind, hit.id);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, flatList, activeId, handleSelect]);

  if (!open) return null;

  const showEmpty =
    debouncedQuery.length >= 2 && !isFetching && data && data.total === 0;
  const showResults = data && data.total > 0;
  const showHint = debouncedQuery.length < 2 && !isFetching;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-title"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 px-4 pt-20 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full max-w-[600px] flex-col rounded-lg border shadow-2xl',
          'max-h-[80vh]',
          'border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-4 py-3">
          {isFetching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--fg-muted)]" />
          ) : (
            <Search className="h-4 w-4 shrink-0 text-[var(--fg-muted)]" />
          )}
          <input
            ref={inputRef}
            id="search-modal-title"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveId(null);
            }}
            placeholder="Buscar clientes, processos, documentos..."
            className="flex-1 bg-transparent text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none"
            autoComplete="off"
          />
          {query.length > 0 ? (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => {
                setQuery('');
                setActiveId(null);
                inputRef.current?.focus();
              }}
              className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--fg)]"
            >
              <X className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="rounded border border-[var(--border)] px-1.5 py-0.5 text-xs text-[var(--fg-muted)]">
              ESC
            </kbd>
          )}
        </div>

        {/* Corpo */}
        <div className="overflow-y-auto">
          {/* Dica inicial */}
          {showHint && (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Search className="h-8 w-8 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                Digite ao menos 2 caracteres para buscar
              </p>
            </div>
          )}

          {/* Loading sem dados ainda */}
          {isFetching && !data && (
            <div className="flex items-center justify-center gap-2 px-4 py-10">
              <Loader2 className="h-4 w-4 animate-spin text-[var(--fg-muted)]" />
              <p className="text-sm text-[var(--fg-muted)]">Buscando...</p>
            </div>
          )}

          {/* Sem resultados */}
          {showEmpty && (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Search className="h-8 w-8 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                Nenhum resultado encontrado para &ldquo;{debouncedQuery}&rdquo;
              </p>
            </div>
          )}

          {/* Resultados agrupados */}
          {showResults && (
            <div className="pb-2">
              {hasModule('clients') && (
                <ClientSection
                  clients={data.clients}
                  activeId={activeId}
                  onHover={setActiveId}
                  onSelect={handleSelect}
                />
              )}
              {hasModule('cases') && (
                <CaseSection
                  cases={data.cases}
                  activeId={activeId}
                  onHover={setActiveId}
                  onSelect={handleSelect}
                />
              )}
              {hasModule('documents') && (
                <DocumentSection
                  documents={data.documents}
                  activeId={activeId}
                  onHover={setActiveId}
                  onSelect={handleSelect}
                />
              )}
              {hasModule('agenda') && (
                <TaskSection
                  tasks={data.tasks}
                  activeId={activeId}
                  onHover={setActiveId}
                  onSelect={handleSelect}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {showResults && (
          <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-2">
            <p className="text-xs text-[var(--fg-muted)]">
              {data.total} resultado{data.total !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-3 text-[10px] text-[var(--fg-muted)]">
              <span><kbd className="rounded border border-[var(--border)] px-1 py-0.5">↑↓</kbd> navegar</span>
              <span><kbd className="rounded border border-[var(--border)] px-1 py-0.5">↵</kbd> abrir</span>
              <span><kbd className="rounded border border-[var(--border)] px-1 py-0.5">ESC</kbd> fechar</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
